import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  LayoutPanelLeft,
  Loader2,
  ShieldAlert,
  Menu,
  Shield,
  Upload,
  X,
} from "lucide-react";

import Sidebar from "../components/ui/Sidebar";
import { ocrApi } from "../../services/ocrApi";
import { SmartInbox } from "../components/documents/SmartInbox";
import { DocumentDetailPanel } from "../components/documents/DocumentDetailPanel";
import { DocumentDropzone } from "../components/documents/DocumentDropzone";
import { AttributesView } from "../components/documents/AttributesView";
import { ManageLayoutsView } from "../components/documents/ManageLayoutsView";
import {
  LayoutEditorModal,
  type LayoutEditorMode,
} from "../components/documents/LayoutEditorModal";
import { useResolveOcrAccount } from "../hooks/useResolveOcrAccount";
import type { DocumentRow } from "../../types/documents";

export default function TemplateEnginePage() {
  // Resolve the OCR X-User-Id once per session (POST /document_extraction/account).
  // The layouts / workflows queries below are gated on this so the requests
  // don't go out with a missing X-User-Id header (backend returns empty data
  // when the header isn't set on the first load).
  const {
    xUserId,
    isResolving,
    error: resolveError,
    gate,
  } = useResolveOcrAccount();
  const ocrReady = xUserId != null;
  const orgUnmapped =
    gate.state === "missing-claims" && gate.missingOrgId;

  // Document opened from the SmartInbox row click — drives the slide-in
  // DocumentDetailPanel on the right (mirrors OcrTrackingPage).
  const [openDoc, setOpenDoc] = useState<DocumentRow | null>(null);
  // Header-button overlays — each swaps the main content area for a
  // dedicated full-page view (Manage Layout / Attributes) or opens the
  // upload modal.
  const [manageOpen, setManageOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [attributesOpen, setAttributesOpen] = useState(false);

  // Layouts list — feeds the LayoutEditorModal in edit mode so it can
  // pre-populate from the existing row. The Manage Layout view does its
  // own fetch; this one stays for the editor's lookup.
  const listQuery = useQuery({
    queryKey: ["ocr", "layouts"],
    queryFn: () => ocrApi.listLayouts(),
    // Wait for /account to populate _resolvedXUserId before listing layouts,
    // otherwise the backend returns no rows for the un-authed shape.
    enabled: ocrReady,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Workflows feed the editor modal's dropdown.
  const workflowsQuery = useQuery({
    queryKey: ["ocr", "workflows"],
    queryFn: () => ocrApi.listWorkflows(),
    enabled: ocrReady,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Coerce the workflows response into a string[] regardless of envelope.
  //
  // The backend has shipped three different shapes over time:
  //
  //   1. Bare array of strings:      ["DE_1", "DE_2", ...]
  //   2. Array of row objects:       [{workflow_id: "DE_1"}, ...]
  //   3. Wrapped in `data | workflows | status` as an array of the above.
  //   4. Current canonical shape — `data` is a MAP of workflow_id to
  //      pipeline variants:
  //        { "status": "success",
  //          "data": {
  //            "DE_1": [["DOCUMENT_PREPROCESS", "LINEITEM_EXTRACTION", ...]],
  //            "AL_1": [...],
  //            ...
  //          } }
  //      We return the keys of that map as the workflow ids.
  const workflows = useMemo<string[]>(() => {
    const raw = workflowsQuery.data;
    const fromArray = (arr: unknown[]): string[] =>
      arr
        .map((item) =>
          typeof item === "string"
            ? item
            : typeof (item as { workflow_id?: unknown })?.workflow_id ===
                "string"
            ? String((item as { workflow_id: string }).workflow_id)
            : null,
        )
        .filter((s): s is string => !!s);

    if (Array.isArray(raw)) {
      return fromArray(raw);
    }
    if (raw && typeof raw === "object") {
      const obj = raw as Record<string, unknown>;
      // Array-shaped envelopes.
      const candidates =
        (Array.isArray(obj.data) ? obj.data : null) ??
        (Array.isArray(obj.workflows) ? obj.workflows : null) ??
        (Array.isArray(obj.status) ? obj.status : null);
      if (candidates) {
        return fromArray(candidates);
      }
      // Map-shaped envelopes — `data` (or `workflows`) is a plain object
      // whose keys ARE the workflow ids. This is the current backend shape.
      const mapCandidate =
        (obj.data && typeof obj.data === "object" && !Array.isArray(obj.data)
          ? (obj.data as Record<string, unknown>)
          : null) ??
        (obj.workflows &&
        typeof obj.workflows === "object" &&
        !Array.isArray(obj.workflows)
          ? (obj.workflows as Record<string, unknown>)
          : null);
      if (mapCandidate) {
        return Object.keys(mapCandidate);
      }
    }
    return [];
  }, [workflowsQuery.data]);

  // Layout editor modal state.
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<LayoutEditorMode>("create");
  const [editorLayoutId, setEditorLayoutId] = useState<string | null>(null);

  const openCreate = () => {
    setEditorMode("create");
    setEditorLayoutId(null);
    setEditorOpen(true);
  };
  const openEdit = (layoutId: string) => {
    setEditorMode("edit");
    setEditorLayoutId(layoutId);
    setEditorOpen(true);
  };

  const layouts = listQuery.data?.layouts ?? [];
  const editingLayout =
    editorMode === "edit" && editorLayoutId
      ? layouts.find((l) => l.layout_id === editorLayoutId)
      : undefined;

  return (
    <div className="h-screen overflow-hidden bg-gray-50 flex">
      {/* Outer global Sidebar (64 px) */}
      <Sidebar />

      {/* Template Engine lives at the top level of the global sidebar
          now (alongside OCR Tracking), so it no longer renders the Studio
          sub-rail. `min-w-0` is still critical at each flex-child level so
          the SmartInbox table (which carries `min-w-[960px]` internally)
          can scroll horizontally INSIDE its container instead of pushing
          the parent column wider than the viewport — without it the
          header buttons on the right end up clipped. */}
      <div className="flex-1 flex ml-16 min-w-0">
        {/* Main content area */}
        <div className="flex-1 overflow-hidden flex flex-col bg-white min-w-0">
          {attributesOpen ? (
            <AttributesView onBack={() => setAttributesOpen(false)} />
          ) : manageOpen ? (
            <ManageLayoutsView
              onBack={() => setManageOpen(false)}
              onCreate={openCreate}
              onEdit={openEdit}
            />
          ) : (
          <>
          {/* Page header — mirrors the Layout Config screen in
              user_interface-theme_tailwind_app: blue layout icon on the
              left with title + subtitle, three pill buttons on the right
              (Attributes / Manage Layout / Upload document). */}
          <header className="border-b border-gray-200 px-6 py-4 flex-shrink-0">
            <div className="flex flex-wrap items-center justify-between gap-y-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="p-3 bg-blue-50 rounded-xl flex-shrink-0">
                  <LayoutPanelLeft size={22} className="text-blue-600" />
                </div>
                <div className="min-w-0">
                  <h1 className="text-2xl font-semibold text-gray-900 truncate">
                    Template Engine
                  </h1>
                  <p className="text-sm text-gray-500 truncate">
                    Configure layout parameters, set up attributes, and monitor document processing.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setAttributesOpen(true)}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                >
                  <Menu size={16} className="text-gray-500" />
                  Attributes
                </button>
                <button
                  type="button"
                  onClick={() => setManageOpen(true)}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                >
                  <Shield size={16} className="text-gray-500" />
                  Manage Layout
                </button>
                <button
                  type="button"
                  onClick={() => setUploadOpen(true)}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
                >
                  <Upload size={16} />
                  Upload document
                </button>
              </div>
            </div>
          </header>

          {/* Body — left list + right detail. Gate the data widgets on the
              /account resolve completing so the layouts/workflows queries
              don't fire before _resolvedXUserId is populated (which would
              return empty data for the un-authed shape). */}
          {!ocrReady ? (
            <div className="flex flex-1 items-center justify-center px-6 py-16 text-center">
              {orgUnmapped ? (
                <div className="max-w-md">
                  <div className="mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-rose-50 text-rose-600 ring-1 ring-rose-200">
                    <ShieldAlert className="h-7 w-7" />
                  </div>
                  <h2 className="text-base font-semibold text-gray-900">
                    Your account isn't mapped to an organization
                  </h2>
                  <p className="mt-2 text-sm text-gray-500">
                    The Template Engine can't load layouts until your
                    administrator maps your account to an organization.
                    After they do, sign out and back in to pick up the
                    change.
                  </p>
                </div>
              ) : resolveError ? (
                <div className="max-w-md">
                  <div className="mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-rose-50 text-rose-600 ring-1 ring-rose-200">
                    <ShieldAlert className="h-7 w-7" />
                  </div>
                  <h2 className="text-base font-semibold text-gray-900">
                    Couldn't load your OCR account
                  </h2>
                  <p className="mt-2 text-sm text-gray-500">
                    {resolveError.message ||
                      "We couldn't reach the account service. Try refreshing the page."}
                  </p>
                </div>
              ) : (
                <div>
                  <Loader2 className="mx-auto h-7 w-7 animate-spin text-indigo-600" />
                  <p className="mt-3 text-sm font-medium text-gray-700">
                    {isResolving ? "Loading your account…" : "Initializing…"}
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Setting up your OCR session before loading layouts.
                  </p>
                </div>
              )}
            </div>
          ) : (
          <div className="flex flex-1 overflow-hidden min-w-0">
            {/* Full-width Smart Inbox — the table matches the Layout
                Config screen in user_interface-theme_tailwind_app: status
                preset pills, filename search, columns/refresh actions,
                and the verification-status badges (Verify AI Template /
                Rule Verification / Final Checker — see
                VerificationStatusBadges). */}
            <main className="flex-1 p-6 overflow-auto min-w-0 min-h-0">
              {/* `dataSource="configStudio"` swaps the inbox query from
                  /tracking_page to /config_studio/homepage — the same
                  pivot the reference makes in tools/index.jsx, so this
                  inbox lists docs uploaded for layout configuration
                  (config_studio_tracking) instead of the full pipeline. */}
              <SmartInbox
                onOpenDocument={(doc) => setOpenDoc(doc)}
                dataSource="configStudio"
              />
            </main>

            {/* Slide-in detail rail for the inbox row click. */}
            <div
              className={`bg-white border-l border-gray-200 flex flex-col min-h-0 transition-all duration-300 ease-in-out ${
                openDoc ? "w-96 opacity-100" : "w-0 opacity-0 overflow-hidden"
              }`}
            >
              {openDoc ? (
                <DocumentDetailPanel
                  doc={openDoc}
                  onClose={() => setOpenDoc(null)}
                  tableName="config_studio_tracking"
                />
              ) : null}
            </div>
          </div>
          )}
          </>
          )}
        </div>
      </div>

      {/* ── Upload document modal ───────────────────────────────────
          Single-file Layout Config upload — matches the reference design
          (LAYOUT row, File Name dropzone, Cancel + Done in footer). The
          dropzone auto-closes the modal on a successful upload. */}
      {uploadOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 backdrop-blur-sm p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Upload document"
          onClick={(e) => {
            if (e.target === e.currentTarget) setUploadOpen(false);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") setUploadOpen(false);
          }}
          tabIndex={-1}
        >
          <div className="w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-2xl bg-white shadow-2xl flex flex-col">
            <header className="flex items-center justify-between border-b border-gray-100 px-6 py-5 flex-shrink-0">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50">
                  <Upload className="h-5 w-5 text-blue-600" />
                </span>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Upload document</h2>
                  <p className="text-xs text-gray-500">
                    Drop one or more files. Pipeline progress shows in the live queue on the right.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setUploadOpen(false)}
                className="rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Close upload dialog"
              >
                <X className="h-5 w-5" />
              </button>
            </header>
            <div className="flex-1 overflow-auto px-6 py-6">
              {/* mode="configStudio" routes the upload through
                  /config_studio/manual_upload so the doc lands in
                  the Layout Config inbox. */}
              <DocumentDropzone
                mode="configStudio"
                onUploaded={() => setUploadOpen(false)}
              />
            </div>
            <footer className="flex items-center justify-end gap-3 border-t border-gray-100 px-6 py-4 flex-shrink-0">
              <button
                type="button"
                onClick={() => setUploadOpen(false)}
                className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-5 py-2 text-sm font-semibold text-blue-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled
                className="inline-flex items-center rounded-lg bg-gray-200 px-5 py-2 text-sm font-semibold text-gray-400 cursor-not-allowed"
                title="Drop a file in the dropzone to upload"
              >
                Done
              </button>
            </footer>
          </div>
        </div>
      ) : null}

      {/* Create / Edit layout modal */}
      <LayoutEditorModal
        open={editorOpen}
        mode={editorMode}
        initialLayoutId={editorLayoutId ?? undefined}
        initial={editingLayout ?? undefined}
        workflows={workflows}
        workflowsLoading={workflowsQuery.isLoading}
        onClose={() => setEditorOpen(false)}
      />
    </div>
  );
}
